<script setup lang="ts">
import { computed, ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import yup from '@/plugins/yup'
import InputGroup from '@/components/Auths/InputGroup.vue'
import { ValidationError } from 'yup'
import { MaintenanceTypeCategory, MaintenanceTypeArea, translateMaintenanceCategory, translateMaintenanceArea } from '@/interfaces/enums'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import IcEditPencil from '@/components/Icons/IcEditPencil.vue'
import router from '@/router'
import { useMaintenanceStore } from '@/store/maintenance'
import { ICreateMaintenanceType } from '@/interfaces/api/maintenance'

const { t } = useI18n()
const isLoading = ref(false)

const { createMaintenanceType } = useMaintenanceStore()

const errors = ref({
  name: '',
  description: '',
  category: '',
  area: '',
})

const categoryOptions = computed(() => {
  return Object.values(MaintenanceTypeCategory).map((category) => ({
    name: translateMaintenanceCategory(category),
    value: category,
  }))
})

const areaOptions = computed(() => {
  return Object.values(MaintenanceTypeArea).map((area) => ({
    name: translateMaintenanceArea(area),
    value: area,
  }))
})

const formData = ref({
  name: '',
  description: '',
  category: '',
  area: '',
})

const labels = ref({
  name: t('maintenances.name'),
  description: t('maintenances.description'),
})

const placeholders = ref({
  name: t('maintenances.placeholders.type_name'),
  description: t('maintenances.placeholders.description'),
})

const maintenanceTypeSchema = yup.object({
  name: yup
    .string()
    .required(t('errors.required', { label: t('maintenances.name') }))
    .min(3, t('errors.minLength', { label: t('maintenances.name'), min: '3' })),
  description: yup.string(),
  category: yup.string().required(t('errors.required', { label: t('maintenances.category') })),
  area: yup.string().required(t('errors.required', { label: t('maintenances.area') })),
})

function handleAreaSelected(option: { value: MaintenanceTypeArea }) {
  formData.value.area = option.value
}

function handleCategorySelected(option: { value: MaintenanceTypeCategory }) {
  formData.value.category = option.value
}

function setErrors(validationErrors: ValidationError) {
  errors.value = {
    name: '',
    description: '',
    category: '',
    area: '',
  }
  validationErrors.inner.forEach((error) => {
    if (error.path) {
      errors.value[error.path as keyof typeof errors.value] = error.message
    }
  })
}

async function submitMaintenanceTypeForm() {
  const values: ICreateMaintenanceType = {
    name: formData.value.name,
    description: formData.value.description,
    category: formData.value.category,
    area: formData.value.area,
  }
  try {
    await maintenanceTypeSchema.validate(values, { abortEarly: false })
    isLoading.value = true
    await createMaintenanceType(values)
    isLoading.value = false
  } catch (err) {
    if (err instanceof ValidationError) {
      setErrors(err)
    }
  }
}

const cancel = () => {
  router.back()
}
</script>

<template>
  <form @submit.prevent="submitMaintenanceTypeForm">
    <div>
      <h2 class="mb-4 border-b border-gray-300 pb-3 text-lg font-semibold">{{ t('maintenance_create.body.subtitle_basic_info') }}</h2>
      <div class="flex flex-col gap-4">
        <div class="w-full md:w-[48%]">
          <InputGroup
            v-model="formData.name"
            :label="labels.name"
            :placeholder="placeholders.name"
            name="name"
            type="text"
            class="w-full"
            :error-text="errors.name"
            required />
        </div>

        <div class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black dark:text-white">
            {{ t('maintenances.category') }}
          </label>
          <Dropdown
            v-model="formData.category"
            name="category"
            :label="t('maintenances.category')"
            :options="categoryOptions"
            :placeholder="t('maintenances.placeholders.select_category')"
            :label-attribute="'name'"
            @option-selected="handleCategorySelected"
            :errorMessage="errors.category" />
        </div>

        <div class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black dark:text-white">
            {{ t('maintenances.area') }}
          </label>
          <Dropdown
            v-model="formData.area"
            name="area"
            :label="t('maintenances.area')"
            :options="areaOptions"
            :placeholder="t('maintenances.placeholders.select_area')"
            :label-attribute="'name'"
            @option-selected="handleAreaSelected"
            :errorMessage="errors.area" />
        </div>

        <div class="w-full">
          <label class="text-md mb-3 block font-medium text-black dark:text-white" for="bio">Descrição</label>
          <div class="relative">
            <IcEditPencil />
            <textarea
              v-model="formData.description"
              class="w-full border border-stroke bg-white py-3 pl-11.5 pr-4.5 font-normal text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
              name="bio"
              id="bio"
              rows="6"
              placeholder="Escreva sua descrição aqui"></textarea>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6 flex justify-end gap-4">
      <button type="button" @click="cancel" class="border border-stroke px-6 py-2 font-medium text-black hover:shadow">Cancelar</button>
      <button type="submit" class="bg-primary px-6 py-2 font-medium text-white hover:bg-opacity-90">
        <span v-if="isLoading">
          <svg class="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0..." />
          </svg>
        </span>
        <span v-else>Salvar</span>
      </button>
    </div>
  </form>
</template>
